import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { useAppSelector } from '@/store';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { DesktopSectionHeader } from '@/components/residents/desktop/myAccount/DesktopSectionHeader';
import { useConfirmModal } from '@/hooks/common/useConfirmModal';
import { useDeleteAccountMutation } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { strings } from '@/constants/residents/strings';

export const AccountInformationView: React.FC = () => {
  const user = useAppSelector((s) => (s as any).auth?.user);
  const name = useMemo(() => {
    const first = user?.firstName || '';
    const last = user?.lastName || '';
    const full = `${first} ${last}`.trim();
    return full.length > 0 ? full : strings.account.guestAccountName;
  }, [user?.firstName, user?.lastName]);

  const email = user?.email || '';
  const photoUrl = (user as any)?.profilePictureUrl || '';

  const { visible: confirmVisible, open: openConfirm, close: closeConfirm } = useConfirmModal(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteAccount] = useDeleteAccountMutation();
  const { showSuccess, showError } = useToast();

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      const resp = await deleteAccount().unwrap();
      if ( (resp as any)?.success ) {
        closeConfirm();
        showSuccess('Account deleted');
      } else {
        closeConfirm();
        showError('Failed to delete account');
      }
    } catch (_e) {
      closeConfirm();
      showError('Failed to delete account');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={styles.root}>
      <DesktopSectionHeader
        title={strings.account.informationTitle}
        containerStyle={styles.header}
        titleStyle={styles.headerTitle}
      />
      <View style={styles.body}>
        <View style={styles.avatarColumn}>
          <View style={styles.avatarCircle}>
            {photoUrl ? (
              <Image source={{ uri: photoUrl }} style={styles.avatarImg} />
            ) : (
              <RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER width={'50%'} height={'50%'} />
            )}
          </View>
          <Text style={styles.nameText}>{name}</Text>
          <Text style={styles.emailText}>{email}</Text>
        </View>
        <View pointerEvents="none" style={styles.logoContainer}>
          <View style={styles.logoInner}>
            <RESIDENTS_SVG_COMPONENTS.RESIO_LOGO_FADED width={160} height={190} />
          </View>
        </View>
      </View>

      <View style={styles.footer}>
        <TouchableOpacity accessibilityRole="button" onPress={openConfirm}>
          <Text style={styles.deleteLink}>{strings.account.deleteAccountLink}</Text>
        </TouchableOpacity>
      </View>

      <WebConfirmModal
        visible={confirmVisible}
        title={strings.account.deleteAccountConfirmTitle}
        message={strings.account.deleteAccountConfirmMessage}
        confirmText={strings.alert.delete}
        cancelText={strings.alert.cancel}
        onConfirm={handleDelete}
        onCancel={closeConfirm}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  headerTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '800',
    fontSize: 18,
  },
  body: {
    flex: 1,
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
  },
  avatarColumn: {
    alignItems: 'flex-start',
    gap: 14,
  },
  avatarCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF1A',
  },
  avatarImg: {
    width: 72,
    height: 72,
  },
  logoContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
  },
  logoInner: {
    position: 'absolute',
    right: -30,
    bottom: -50,
    opacity: 0.3,
  },
  nameText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 18,
    fontWeight: '600',
  },
  emailText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    opacity: 0.9,
    marginTop: 2,
  },
  footer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    marginTop: 'auto',
  },
  deleteLink: {
    color: '#9CA3AF',
    fontSize: 12,
    fontWeight: '500',
  },
});

export default AccountInformationView;


